import type { Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { combineSx } from '@/utils/combineSx';
import { Label } from '../../Label';
import { MAndAIcon } from './MAndAIcon';
import type { CompanyParticipation, MetricValue } from './types';

const metricStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    gap: '0.375rem',
    position: 'relative',
  },
  value: (theme: Theme) => ({
    fontSize: theme.fontSizes.smaller,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.5,
    position: 'relative',
    gap: '0.25rem',
    alignItems: 'flex-start',
    fontVariantNumeric: 'tabular-nums',
    textWrap: 'balance',
    whiteSpace: 'wrap',
  }),
  unit: (theme: Theme) => ({
    fontSize: theme.fontSizes.smallest,
    fontWeight: '400',
    color: theme.palette.baseColors.deepSeaNavy.c100,
    padding: '0.125rem',
    display: 'inline-block',
    lineHeight: '1',
    verticalAlign: 'text-bottom',
  }),
};

type MetricProps = {
  label: string;
  value: MetricValue | CompanyParticipation[];
  unit?: string;
};

const isCompanyParticipationArray = (
  value: MetricValue | CompanyParticipation[],
): value is CompanyParticipation[] => {
  return Array.isArray(value);
};

const renderValue = (value: MetricValue | CompanyParticipation[]): ReactNode => {
  if (!value) {
    return '—';
  }

  if (isCompanyParticipationArray(value)) {
    return value.map((participation, index) => (
      <Fragment key={participation.text}>
        <Box
          component='span'
          sx={combineSx(
            { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 },
            { mb: index < value.length - 1 ? 'var(--gap-section)' : 0 },
          )}
        >
          {participation.text}
          <MAndAIcon enabled={participation.needsIcon} />
        </Box>
      </Fragment>
    ));
  }

  return value;
};

const Metric = ({ label, value, unit }: MetricProps) => {
  const displayedValue = renderValue(value);
  const hasValue = !!value;

  return (
    <Box sx={combineSx(metricStyles.container, { opacity: hasValue ? 1 : 0.4 })} data-id='metric'>
      <Label label={label} />
      <Typography sx={metricStyles.value}>
        {displayedValue}
        {hasValue && unit && (
          <Typography component='sub' sx={metricStyles.unit}>
            {unit}
          </Typography>
        )}
      </Typography>
    </Box>
  );
};

export { Metric };
